import type { ReactNode } from 'react';

/**
 * The little Markdown the model writes (paragraphs, bullet and numbered lists, one
 * level of nesting, **bold**, headings and simple tables), rendered as React
 * elements: no HTML from the model ever reaches the page, and no library is needed.
 * Times (18:40) stand out, so a schedule reads at a glance in the street.
 */
export function Markdown({ text }: { text: string }): ReactNode {
  return <>{blocks(text)}</>;
}

type Item = { text: string; children: string[] };

function blocks(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    const list = listKind(line);
    if (list) {
      const items: Item[] = [];
      while (i < lines.length) {
        const l = lines[i]!;
        if (!l.trim()) {
          // A blank line ends the list unless more items follow
          if (i + 1 < lines.length && listKind(lines[i + 1]!)) {
            i++;
            continue;
          }
          break;
        }
        const indented = /^\s{2,}/.test(l);
        if (indented && items.length > 0) {
          items.at(-1)!.children.push(stripMarker(l.trim()));
        } else if (listKind(l)) {
          items.push({ text: stripMarker(l.trim()), children: [] });
        } else if (items.length > 0) {
          items.at(-1)!.text += ` ${l.trim()}`;
        } else break;
        i++;
      }
      const Tag = list === 'ol' ? 'ol' : 'ul';
      out.push(
        <Tag
          key={out.length}
          className={`my-2 space-y-1.5 ps-5 marker:text-gold-700 dark:marker:text-gold ${list === 'ol' ? 'list-decimal marker:font-semibold' : 'list-disc'}`}
        >
          {items.map((item, k) => (
            <li key={k}>
              {inline(item.text)}
              {item.children.length > 0 && (
                <ul className="mt-1 list-[circle] space-y-0.5 ps-5 text-[0.97em]">
                  {item.children.map((c, m) => (
                    <li key={m}>{inline(c)}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </Tag>,
      );
      continue;
    }
    if (isTableRow(line) && i + 1 < lines.length && isTableRule(lines[i + 1]!)) {
      const head = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i]!)) rows.push(cells(lines[i++]!));
      out.push(
        <div key={out.length} className="my-3 overflow-x-auto first:mt-0">
          <table className="w-full border-collapse text-[0.95em] leading-snug">
            <thead>
              <tr className="border-b border-gold/60">
                {head.map((h, k) => (
                  <th key={k} className="px-2 py-1.5 text-left font-semibold first:ps-0">
                    {inline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, k) => (
                <tr key={k} className="border-b border-ink/8 last:border-0 dark:border-cream/10">
                  {head.map((_, m) => (
                    <td key={m} className="px-2 py-1.5 align-top first:ps-0">
                      {inline(r[m] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    if (heading) {
      out.push(
        <p
          key={out.length}
          className="mt-3 mb-1 font-serif text-xl leading-tight font-semibold text-purple first:mt-0 dark:text-gold-300"
        >
          {inline(heading[1]!)}
        </p>,
      );
      i++;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !listKind(lines[i]!)) {
      para.push(lines[i]!.trim());
      i++;
    }
    out.push(
      <p key={out.length} className="my-2 first:mt-0 last:mb-0">
        {para.flatMap((p, k) => (k === 0 ? [inline(p)] : [<br key={`br${k}`} />, inline(p)]))}
      </p>,
    );
  }
  return out;
}

function listKind(line: string): 'ul' | 'ol' | null {
  const t = line.trimStart();
  if (/^[-*•]\s+/.test(t)) return 'ul';
  if (/^\d+[.)]\s+/.test(t)) return 'ol';
  return null;
}

const stripMarker = (l: string) => l.replace(/^([-*•]|\d+[.)])\s+/, '');

const isTableRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
const isTableRule = (l: string) => /^\s*\|(\s*:?-{2,}:?\s*\|)+\s*$/.test(l);
const cells = (l: string) =>
  l
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((c) => c.trim());

/** **bold** (names of brotherhoods, streets…) and *italic*; times stand out. */
function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g).flatMap((part, k) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return (
        <strong key={k} className="font-semibold text-purple dark:text-gold-300">
          {times(part.slice(2, -2), k)}
        </strong>
      );
    }
    if (/^\*[^*\s][^*]*\*$/.test(part)) return <em key={k}>{part.slice(1, -1)}</em>;
    return times(part, k);
  });
}

/** "a las 18:40" → the time in semibold, with even digits. */
function times(text: string, key: number): ReactNode[] {
  return text.split(/(\b\d{1,2}:\d{2}\b)/g).map((part, k) =>
    /^\d{1,2}:\d{2}$/.test(part) ? (
      <span key={`${key}-${k}`} className="tabular font-semibold whitespace-nowrap">
        {part}
      </span>
    ) : (
      part
    ),
  );
}
